// Shared Tailwind class constants for the project's dp-modal skin used by
// several upload/edit dialogs.

// Apply alongside the "dp-modal" class on the modal root. Styles the nested
// modal pieces (dialog, header, footer, title, labels) so each consumer
// doesn't have to repeat them.


export const modalParentClasses = {
    root:
        'fixed inset-0 z-[1055] flex justify-center items-start pt-5 ' +
        'overflow-x-hidden overflow-y-auto outline-0 bg-black/80',

    dialog:
        'relative w-full m-0 pointer-events-none ' +
        'min-[576px]:mx-auto',

    // Ivory panel, matching the page surface; white stays reserved for the inputs inside.
    content:
        'relative flex w-full flex-col pointer-events-auto ' +
        'bg-surface bg-clip-padding border border-border rounded-lg outline-0',

    header:
        'flex shrink-0 items-start justify-between ' +
        'p-4 border-b border-border-light ' +
        'rounded-t-[calc(0.5rem_-_1px)]',

    title:
        "mb-0 leading-normal text-2xl font-semibold text-content !font-['Open_Sans',serif]",

    body:
        'relative flex-auto max-h-[70vh] overflow-y-auto p-4',

    footer:
        'flex flex-wrap shrink-0 items-center justify-end ' +
        'px-5 py-2.5 [&>*]:m-1',
};

export const contentOverflowModalClasses =
    'max-h-[65vh] overflow-y-auto p-3';

export const uploadTemplateModalClasses =
    '[&_.dp-modal-dialog]:max-w-[468px] ' +
    '[&_.dp-modal-dialog.dp-modal-lg]:max-w-[840px] ' +
    '[&_.dp-modal-dialog.dp-modal-lg_.card-body]:p-3 ' +
    '[&_.dp-modal-dialog.dp-modal-lg_.card-body]:max-h-[66vh] ' +
    '[&_.dp-modal-dialog.dp-modal-lg_.card-body]:overflow-y-auto ' +
    '[&_.dp-modal-header]:border-b-0 ' +
    '[&_.dp-modal-title]:text-2xl ' +
    '[&_.dp-modal-title]:font-semibold ' +
    '[&_.dp-modal-title]:text-content ' +
    "[&_.dp-modal-title]:font-['Open_Sans',serif]! " +
    '[&_form_label]:text-base ' +
    '[&_form_label]:mb-2 ' +
    '[&_form_label]:text-content';

// Variant used only by TemplateFormattingSettingModal -- wider dialog and a
// borderless header. Header rule overlaps with the upload-template modal
// variant; we expose it separately because the dialog width differs.
export const uploadTemplateSettingModalClasses =
    '[&_.dp-modal-dialog]:max-w-[700px] ' +
    '[&_.dp-modal-header]:border-b-0';

// 800px-wide dialog variant -- used by histograms, company details, and
// medical record view modals. Combine with uploadTemplateModalClasses to
// override its 468px default.
export const mediumModalClasses =
    '[&_.dp-modal-dialog]:max-w-[800px]!';

// 800px-wide dialog with borderless header/footer and themed title -- used
// by user/team/defendant/plaintiff edit modals. The w-full + max-w-200 pair
// forces the dialog past the legacy `width: auto; max-width: 500px` rule on
// `.dp-modal-dialog` in index.css (unlayered legacy beats utilities without `!`).
export const userModalClasses =
    '[&_.dp-modal-dialog]:w-full! ' +
    '[&_.dp-modal-dialog]:max-w-200! ' +
    '[&_.dp-modal-header]:border-b-0 ' +
    '[&_.dp-modal-title]:text-2xl ' +
    '[&_.dp-modal-title]:font-semibold ' +
    '[&_.dp-modal-title]:text-content ' +
    "[&_.dp-modal-title]:font-['Open_Sans',serif]! " +
    '[&_form_label]:text-base ' +
    '[&_form_label]:mb-2 ' +
    '[&_form_label]:text-content';

// 500px-wide dialog -- used by help/warning/case-note modals.
export const noteModalClasses =
    '[&_.dp-modal-dialog]:max-w-[500px]!';

export const modalWidthClasses =
    'w-full! ' +
    'min-[576px]:max-w-[540px]! ' +
    'min-[768px]:max-w-[720px]! ' +
    'min-[992px]:max-w-[960px]! ' +
    'min-[1200px]:max-w-[1140px]! ' +
    'min-[1400px]:max-w-[1320px]!';

export const modalSizeClasses = {
    xlg: modalWidthClasses,
    lg: 'w-full !max-w-[70%]',
    sm: 'w-full !max-w-[500px]',
};

export const modalScrollClasses =
    '[&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-2.5 ' +
    '[&::-webkit-scrollbar-track]:bg-surface-alt [&::-webkit-scrollbar-track]:rounded-[10px] ' +
    '[&::-webkit-scrollbar-thumb]:bg-theme [&::-webkit-scrollbar-thumb]:rounded-[10px]';
